import React from 'react';
import { useTranslation } from 'react-i18next';

export const TokenomicsPage: React.FC = () => {
  const { t } = useTranslation();

  // Helper to render section dividers
  const Divider = () => (
    <div className="flex justify-center my-16">
      <div className="h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent w-full max-w-2xl"></div>
    </div>
  );

  return (
    <div className="min-h-screen relative overflow-hidden py-20 bg-bg-primary">
      {/* Background Elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-900/5 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10 max-w-4xl">

        {/* Header */}
        <div className="text-center mb-20 animate-fade-in">
          <div className="hero-badge inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
            <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('pages.tokenomics.badge')}</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black text-white mb-6 bg-gradient-to-r from-gold to-white bg-clip-text text-transparent">
            {t('pages.tokenomics.title')}
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mx-auto leading-relaxed font-light">
            {t('pages.tokenomics.subtitle')}
          </p>
        </div>

        {/* Token Allocation */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gold mb-6">{t('pages.tokenomics.sections.allocation.title')}</h2>
          <div className="bg-surface/50 backdrop-blur-sm border border-gold/10 rounded-3xl p-8 md:p-12 mb-8 shadow-lg">
            <div className="text-center mb-8">
              <div className="text-5xl font-black text-gold mb-2">{t('pages.tokenomics.sections.allocation.totalSupply')}</div>
              <p className="text-gray-300 text-lg">{t('pages.tokenomics.sections.allocation.description')}</p>
            </div>
          </div>

          {/* Allocation Categories */}
          <div className="grid gap-6">
            {(t('pages.tokenomics.sections.allocation.categories', { returnObjects: true }) as any[]).map((category, idx) => (
              <div key={idx} className="bg-surface/50 backdrop-blur-sm border border-gold/10 rounded-2xl p-6 md:p-8 hover:border-gold/30 transition-all">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                  <h3 className="text-2xl font-bold text-white mb-2 md:mb-0">{category.title}</h3>
                  <div className="flex items-center gap-4">
                    <span className="text-3xl font-black text-gold">{category.percentage}</span>
                    <span className="text-gray-400 text-sm">{category.amount}</span>
                  </div>
                </div>

                <p className="text-gray-300 font-medium mb-3">{category.description}</p>
                <ul className="space-y-2 mb-4">
                  {category.items.map((item: string, i: number) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center text-gold text-xs mt-0.5">•</span>
                      <span className="text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>

                {category.vestingPolicy && (
                  <div className="mt-4 pt-4 border-t border-gray-800">
                    <p className="text-gray-400 font-medium mb-2">{category.vestingPolicy}</p>
                    <ul className="space-y-1">
                      {category.vestingItems.map((item: string, i: number) => (
                        <li key={i} className="text-gray-400 text-sm flex items-center gap-2">
                          <span className="text-gold">→</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {category.note && (
                  <div className="mt-4 bg-gold/5 border border-gold/20 rounded-lg p-3">
                    <p className="text-gold text-sm italic">{category.note}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <Divider />

        {/* Vesting & Anti-Dump Mechanism */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gold mb-6">{t('pages.tokenomics.sections.vesting.title')}</h2>
          <div className="bg-gradient-to-br from-surface to-green-900/10 p-8 rounded-2xl border border-green-900/30">
            <p className="text-gray-300 mb-6 text-lg">{t('pages.tokenomics.sections.vesting.intro')}</p>
            <ul className="space-y-3 mb-6">
              {(t('pages.tokenomics.sections.vesting.principles', { returnObjects: true }) as string[]).map((principle, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center text-green-500 mt-1">✓</span>
                  <span className="text-gray-200 text-lg">{principle}</span>
                </li>
              ))}
            </ul>

            <div className="border-t border-gray-800 pt-6">
              <p className="text-gold font-semibold mb-3">{t('pages.tokenomics.sections.vesting.goals')}</p>
              <ul className="grid md:grid-cols-3 gap-4">
                {(t('pages.tokenomics.sections.vesting.goalItems', { returnObjects: true }) as string[]).map((goal, idx) => (
                  <li key={idx} className="bg-surface/50 p-4 rounded-lg border border-gold/10 text-center">
                    <span className="text-gray-200 font-medium">{goal}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <Divider />

        {/* Token Utility */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gold mb-6">{t('pages.tokenomics.sections.utility.title')}</h2>
          <div className="bg-surface/50 backdrop-blur-sm border border-gold/10 rounded-2xl p-8">
            <p className="text-gray-300 mb-6 text-lg">{t('pages.tokenomics.sections.utility.intro')}</p>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              {(t('pages.tokenomics.sections.utility.items', { returnObjects: true }) as string[]).map((item, idx) => (
                <div key={idx} className="bg-surface p-4 rounded-xl border border-gold/5 flex items-start gap-3">
                  <i className="fas fa-star text-gold mt-1"></i>
                  <span className="text-gray-300">{item}</span>
                </div>
              ))}
            </div>
            <p className="text-white font-medium text-center bg-gold/10 p-4 rounded-xl border border-gold/20">{t('pages.tokenomics.sections.utility.conclusion')}</p>
          </div>
        </div>

        <Divider />

        {/* Design Philosophy */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gold mb-8">{t('pages.tokenomics.sections.philosophy.title')}</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {(t('pages.tokenomics.sections.philosophy.principles', { returnObjects: true }) as any[]).map((principle, idx) => (
              <div key={idx} className="bg-surface p-6 rounded-xl border border-gold/10 text-center hover:border-gold/30 transition-colors">
                <h3 className="text-lg font-bold text-gold mb-3">{principle.title}</h3>
                <p className="text-sm text-gray-400">{principle.description}</p>
              </div>
            ))}
          </div>
        </div>

        <Divider />

        {/* Conclusion */}
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-white mb-8">{t('pages.tokenomics.sections.conclusion.title')}</h2>
          <div className="space-y-4 text-gray-300 text-lg leading-relaxed">
            {(t('pages.tokenomics.sections.conclusion.content', { returnObjects: true }) as string[]).map((paragraph, idx) => (
              <p key={idx} className={idx >= 2 ? "text-gold font-semibold text-xl" : ""}>{paragraph}</p>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-20 pt-10 border-t border-gray-800 text-center text-gray-500 text-sm">
          <p>© 2026 IDNGOLD. All Rights Reserved. | Smart Billionaire Indonesia</p>
        </div>

      </div>
    </div>
  );
};